import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router";
import {
  Badge,
  Breakpoints,
  Card,
  Colors,
  Icon,
  type BadgeStatus,
  type IconName,
} from "swing-components";

import { ContentSingleColumn, ProgressIcon, type IconColor } from "~components";
import { GET, translate as t, type KeyWithEmptyFnParam, type SubDocumentsData } from "~utils";

import { createDocumentsTaskUrl } from "../../Routes";
import { ScorePage } from "../../ScoreTemplates";

type Documents = SubDocumentsData;
type Document = SubDocumentsData[number]["documents"][number];
type DocumentCategory = SubDocumentsData[number]["category"];

function toDocumentCard(document: Document) {
  const { id, status, progress, dueInDays, expirationDate, isReadOnly } = document;
  const card = {
    title: t(id as unknown as KeyWithEmptyFnParam),
    isReadOnly,
  } as const;

  switch (status) {
    case "IN_PROGRESS":
      return {
        ...card,
        icon: "Apple",
        color: "BLUE",
        percentage: progress ?? 0,
        ...(dueInDays
          ? { badge: t("DOCS_DUE_IN_DAYS", { dueInDays }), badgeStatus: "primary" }
          : {}),
      };
    case "UNDER_REVIEW":
      return {
        ...card,
        icon: "Hourglass",
        color: "PURPLE",
        percentage: 100,
        badge: t("LABEL_UNDER_REVIEW"),
        badgeStatus: "tertiary",
      };
    case "ACTION_REQUIRED":
      return {
        ...card,
        icon: "Warning",
        color: "YELLOW",
        percentage: 100,
        badge: t("LABEL_ACTION_REQUIRED"),
        badgeStatus: "warning",
      };
    case "COMPLETED":
      return {
        ...card,
        icon: "Check Circle",
        color: "GREEN",
        percentage: 100,
        ...(expirationDate
          ? { badge: t("DOCS_COMPLETED", { expirationDate }), badgeStatus: "medium-light" }
          : {}),
      };
    case "DUE_SOON":
    case "EXPIRES_SOON":
      return {
        ...card,
        icon: "Warning",
        color: "YELLOW",
        percentage: 100,
        ...(dueInDays
          ? { badge: t("DOCS_DUE_IN_DAYS", { dueInDays }), badgeStatus: "warning" }
          : {}),
      };
    case "EXPIRED":
      return {
        ...card,
        icon: "Warning",
        color: "RED",
        percentage: 100,
        badge: t("LABEL_EXPIRED"),
        badgeStatus: "danger",
      };
    case "NOT_STARTED":
      return {
        ...card,
        icon: "Apple",
        color: "BLUE",
        percentage: 0,
        ...(dueInDays
          ? { badge: t("DOCS_DUE_IN_DAYS", { dueInDays }), badgeStatus: "primary" }
          : {}),
      };
    default:
      return {
        ...card,
        icon: "Warning",
        color: "RED",
        percentage: 100,
        badge: t("LABEL_ERROR"),
        badgeStatus: "danger",
      };
  }
}

function DocumentsCard({ document }: { document: Document }) {
  const history = useHistory();
  const card = toDocumentCard(document);

  return (
    <Card
      borderColor={Colors.slate200}
      {...(card.isReadOnly
        ? {}
        : {
            onClick: () => {
              history.push(createDocumentsTaskUrl(document.id.toLowerCase()));
            },
          })}
    >
      <div
        css={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
          cursor: card.isReadOnly ? "default" : "pointer",
        }}
      >
        <ProgressIcon
          icon={card.icon as IconName}
          percentage={card.percentage}
          color={card.color as IconColor}
        />
        <div
          css={{
            marginLeft: "var(--spacing-lg)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing-sm)",
            flex: 1,
            minWidth: 0,
          }}
        >
          <p
            css={{
              fontSize: "var(--swing-font-sm)",
              fontWeight: "var(--swing-font-semibold)",
              margin: 0,
            }}
          >
            {card.title}
          </p>
          {card.badge && <Badge status={card.badgeStatus as BadgeStatus} text={card.badge} />}
        </div>
        {!card.isReadOnly && (
          <span css={{ flexShrink: 0, marginLeft: "var(--spacing-sm)", display: "flex" }}>
            <Icon name="Chevron Right" color={Colors.black} />
          </span>
        )}
      </div>
    </Card>
  );
}

function toDocumentsSection(category: DocumentCategory) {
  switch (category) {
    case "REQUIRED":
      return {
        title: t("DOCS_SECTION_REQUIRED"),
        description: undefined,
      };
    case "OPTIONAL":
      return {
        title: t("DOCS_SECTION_OPTIONAL"),
        description: t("DOCS_SECTION_OPTIONAL_DESC"),
      };
    default:
      return {
        title: undefined,
        description: undefined,
      };
  }
}

function DocumentsSection({
  category,
  children,
}: {
  category: DocumentCategory;
  children: React.ReactNode;
}) {
  const { title, description } = toDocumentsSection(category);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "var(--spacing-xl)" }}>
      <div>
        <h3 css={{ margin: 0 }}>{title}</h3>
        {description && <p css={{ margin: 0, marginTop: "var(--spacing-sm)" }}>{description}</p>}
      </div>
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-lg)",
          [Breakpoints.mediumDeviceAndUp]: { padding: "0 var(--spacing-2xl)" },
        }}
      >
        {children}
      </div>
    </div>
  );
}

function DocumentsEmpty() {
  return (
    <ScorePage title={t("SETTINGS_DOCUMENTS")} hasBack>
      <ContentSingleColumn>
        <div css={{ textAlign: "center", padding: "var(--spacing-lg)" }}>
          {t("DOCS_NO_DOCUMENTS")}
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}

function DocumentsView({ documents }: { documents: Documents }) {
  if (!documents || documents.length === 0) {
    return <DocumentsEmpty />;
  }

  return (
    <ScorePage title={t("SETTINGS_DOCUMENTS")} hasBack>
      <ContentSingleColumn>
        <div
          css={{
            padding: "var(--spacing-lg)",
            gap: "var(--spacing-6xl)",
            display: "flex",
            flexDirection: "column",
            [Breakpoints.mediumDeviceAndUp]: {
              padding: "0px var(--spacing-lg) var(--spacing-lg)",
            },
          }}
        >
          {documents.map(({ category, documents }) => {
            return (
              <DocumentsSection key={category} category={category}>
                {documents.map((document) => (
                  <DocumentsCard key={document.id} document={document} />
                ))}
              </DocumentsSection>
            );
          })}
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}

export function Documents() {
  const {
    data: documents,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["sub", "documents"],
    queryFn: () => GET("/api/sub/documents"),
    select: (response) => response.data,
  });

  if (isPending) {
    return (
      <ScorePage title={t("SETTINGS_DOCUMENTS")} hasBack isLoading>
        <ContentSingleColumn>{null}</ContentSingleColumn>
      </ScorePage>
    );
  }

  if (isError) {
    return (
      <ScorePage title={t("SETTINGS_DOCUMENTS")} hasBack>
        <ContentSingleColumn>
          <div css={{ textAlign: "center", padding: "var(--spacing-lg)" }}>
            {t("ERROR_GENERAL")}
          </div>
        </ContentSingleColumn>
      </ScorePage>
    );
  }

  return <DocumentsView documents={documents ?? []} />;
}
